import type { AppLocale } from '@/i18n/locales'
import type { BuffSpeechPreferences } from './speech-types'
import { SystemVoices, speechLanguage } from './system-voices'
import { ManboAudio } from './manbo-audio'
import { usesManbo } from './manbo'
import { db } from '@/services/database'

const START_TIMEOUT_MS = 2000
const RETRY_DELAY_MS = 100

export interface SpeechSentence {
  text: string
  locale: AppLocale
  preferences: BuffSpeechPreferences
  preview?: boolean
  customAudioId?: string
  key?: string
}
export interface SpeechDiagnostic {
  key?: string
  attempt: number
  voice?: { name: string; voiceURI: string; lang: string; localService: boolean }
  speaking?: boolean
  pending?: boolean
  paused?: boolean
}
export interface SpeechCallbacks {
  valid: () => boolean
  started: () => void
  completed: () => void
  failed: (reason: string) => void
  cancelled: () => void
  preparing?: (value: boolean) => void
}
export class BuffSpeechPlayer {
  readonly voices = new SystemVoices()
  readonly manbo = new ManboAudio()
  private disposed = false
  private audio?: HTMLAudioElement
  private unlocking?: Promise<void>
  private unlocked = false
  private cancelPlayback?: () => void
  constructor(private readonly report: (type: string, reason?: string, detail?: SpeechDiagnostic) => void = () => {}) {}
  unlock(): void {
    try { if (typeof speechSynthesis !== 'undefined') speechSynthesis.resume?.() } catch { /* Playback reports the actual error. */ }
    if (typeof Audio === 'function' && !this.unlocked && !this.unlocking && (!this.audio || this.audio.paused)) {
      this.audio ??= new Audio()
      this.audio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAACAgICA'
      try {
        const unlocking = Promise.resolve(this.audio.play())
        this.unlocking = unlocking
        void unlocking.then(() => { if (this.unlocking === unlocking) this.unlocked = true }, () => {})
          .finally(() => { if (this.unlocking === unlocking) this.unlocking = undefined })
      }
      catch { /* Report blocked playback when the user previews or an alert plays. */ }
    }
  }
  play(sentence: SpeechSentence, callbacks: SpeechCallbacks): () => void {
    const custom = sentence.customAudioId !== undefined
    const controller = new AbortController()
    let finished = false, started = false, utterance: SpeechSynthesisUtterance | undefined
    let audioCleanup = () => {}
    let audioFailure: string | undefined
    let usingSystem = false
    let attempt = 0, generation = 0
    let systemVoice: SpeechSynthesisVoice | undefined
    let voiceDetail: SpeechDiagnostic['voice']
    let retryTimeout: ReturnType<typeof setTimeout> | undefined
    let startupTimeout: ReturnType<typeof setTimeout> | undefined
    const clearStartup = () => { clearTimeout(startupTimeout); startupTimeout = undefined }
    const report = (type: string, reason?: string) => this.report(type, reason, {
      key: sentence.key, attempt, voice: voiceDetail,
      ...(typeof speechSynthesis === 'undefined' ? {} : {
        speaking: speechSynthesis.speaking, pending: speechSynthesis.pending, paused: speechSynthesis.paused,
      }),
    })
    const clearNative = (cancel = false) => {
      generation++
      clearStartup(); clearTimeout(retryTimeout); retryTimeout = undefined
      const previous = utterance; utterance = undefined
      if (previous) {
        previous.onstart = null; previous.onend = null; previous.onerror = null
        if (cancel) speechSynthesis.cancel()
      }
    }
    const start = () => {
      clearStartup()
      if (started) return
      started = true; callbacks.preparing?.(false); callbacks.started()
    }
    const done = (error?: string) => {
      if (finished || controller.signal.aborted) return
      finished = true
      if (!error && !started) error = 'audio-not-started'
      report(error ? 'failed' : 'completed', error)
      clearNative(!!error); audioCleanup(); callbacks.preparing?.(false)
      if (error) callbacks.failed(error); else callbacks.completed()
    }
    const valid = () => {
      if (controller.signal.aborted || finished || this.disposed) return false
      if (started) return true // Finish the current sentence once playback has started.
      if (callbacks.valid()) return !finished && !controller.signal.aborted
      if (finished || controller.signal.aborted) return false
      finished = true; report('cancelled', 'invalidated')
      clearNative(true); audioCleanup(); callbacks.preparing?.(false); callbacks.cancelled(); return false
    }
    const systemFailure = (reason: string) => {
      if (!valid()) return
      if (!started && attempt < 2 && (reason === 'speech-start-timeout' || reason === 'audio-busy')) {
        report('retry', reason)
        // Invalidate saved callbacks before cancel(), which can itself emit events.
        clearNative(true)
        retryTimeout = setTimeout(system, RETRY_DELAY_MS)
      } else done(reason)
    }
    const native = (token: number) => {
      if (token !== generation || !valid()) return
      if (!this.voices.supported) { done(custom ? 'custom-audio-local-unavailable' : audioFailure === 'not-allowed' ? audioFailure : 'speech-unavailable'); return }
      const preferred = sentence.preferences.voices?.[speechLanguage(sentence.locale)]
      const voice = systemVoice ?? (custom ? this.voices.selectLocal(sentence.locale, preferred) : this.voices.select(sentence.locale, preferred))
      if (!voice) { done(custom ? 'custom-audio-local-unavailable' : audioFailure === 'not-allowed' ? audioFailure : this.voices.voices.length ? 'voice-missing' : 'voices-unavailable'); return }
      systemVoice = voice
      voiceDetail = { name: voice.name, voiceURI: voice.voiceURI, lang: voice.lang, localService: voice.localService }
      try { utterance = new SpeechSynthesisUtterance(sentence.text) } catch { done('audio-error'); return }
      utterance.lang = voice.lang.replaceAll('_', '-'); utterance.voice = voice
      utterance.volume = Math.max(0, Math.min(1, sentence.preferences.volume))
      utterance.rate = Math.max(.5, Math.min(2, sentence.preferences.rate))
      utterance.onstart = () => {
        if (token !== generation || !valid()) return
        report('started', 'system')
        start()
      }
      utterance.onend = () => { if (token === generation && !finished && !controller.signal.aborted) { report('ended', 'system'); done() } }
      utterance.onerror = event => { if (token === generation) systemFailure(event.error) }
      report('provider', 'system')
      report('requested', 'system')
      try { speechSynthesis.speak(utterance) } catch (error) { done((error instanceof Error || error instanceof DOMException) && error.name === 'NotAllowedError' ? 'not-allowed' : 'audio-error') }
    }
    const system = () => {
      if (!valid()) return
      usingSystem = true
      clearStartup()
      const token = ++generation
      attempt++
      startupTimeout = setTimeout(() => {
        if (token === generation && valid()) { report('startup-timeout', 'system'); systemFailure('speech-start-timeout') }
      }, START_TIMEOUT_MS)
      const readable = this.voices.refresh()
      if (readable && !this.voices.voices.length) void this.voices.wait(controller.signal).then(() => native(token))
      else native(token)
    }
    const fallback = (reason: string) => {
      if (usingSystem || finished || controller.signal.aborted) return
      if (!valid()) return
      usingSystem = true; audioFailure = reason
      clearStartup(); audioCleanup(); callbacks.preparing?.(false)
      if (reason === 'not-allowed') this.unlocked = false
      report('fallback', reason)
      if (custom && reason === 'not-allowed') done(reason)
      else system()
    }
    const recorded = async () => {
      let ownedUrl: string | undefined
      const releaseUrl = () => { if (ownedUrl) { URL.revokeObjectURL(ownedUrl); ownedUrl = undefined } }
      try {
        if (sentence.preview && !custom) {
          callbacks.preparing?.(true)
          await this.manbo.wait(controller.signal)
        }
        if (!valid()) return
        startupTimeout = setTimeout(() => {
          if (!usingSystem && valid()) {
            report('startup-timeout', custom ? 'custom' : 'manbo')
            // A hung gesture promise must not block subsequent reminders either.
            if (this.unlocking) { this.unlocking = undefined; this.audio?.pause() }
            fallback(custom ? 'custom-audio-timeout' : 'manbo-start-timeout')
          }
        }, custom ? 5000 : START_TIMEOUT_MS)
        let url: string | null
        if (custom) {
          const clip = await db.customAudioClips.get(sentence.customAudioId!)
          if (usingSystem || !valid()) return
          url = clip ? ownedUrl = URL.createObjectURL(clip.blob) : null
          audioCleanup = releaseUrl
        } else url = await this.manbo.cachedUrl(sentence.text)
        if (usingSystem || !valid()) return
        // Finish the gesture unlock before replacing its source with the real MP3.
        await this.unlocking
        if (usingSystem || !valid()) return
        callbacks.preparing?.(false)
        if (!url) { fallback(custom ? 'custom-audio-missing' : 'manbo-not-ready'); return }
        const audio = this.audio ??= new Audio()
        let timeout: ReturnType<typeof setTimeout> | undefined
        const clearAudio = () => {
          clearTimeout(timeout)
          audio.oncanplay = null; audio.onplaying = null; audio.onended = null; audio.onerror = null
          audio.pause(); audio.removeAttribute('src'); audio.load()
          releaseUrl()
        }
        audioCleanup = clearAudio
        audio.volume = Math.max(0, Math.min(1, sentence.preferences.volume))
        audio.onplaying = () => {
          if (usingSystem || !valid()) return
          clearTimeout(timeout)
          this.unlocked = true
          if (!started) { if (!custom) this.manbo.markUsed(sentence.text); report('started', custom ? 'custom' : 'manbo') }
          start()
          timeout = setTimeout(() => fallback(custom ? 'custom-audio-invalid' : 'manbo-audio'), (Number.isFinite(audio.duration) ? audio.duration / audio.playbackRate * 1000 : 30_000) + 5000)
        }
        audio.onended = () => { if (!usingSystem) done() }
        audio.onerror = () => fallback(custom ? 'custom-audio-invalid' : 'manbo-audio')
        audio.oncanplay = () => {
          if (usingSystem || !valid()) return
          audio.oncanplay = null
          audio.currentTime = 0
          audio.playbackRate = custom ? 1 : Math.max(.5, Math.min(2, sentence.preferences.rate))
          report('provider', custom ? 'custom' : 'manbo')
          void audio.play().catch(error => fallback((error instanceof Error || error instanceof DOMException) && error.name === 'NotAllowedError' ? 'not-allowed' : custom ? 'custom-audio-invalid' : 'manbo-audio'))
        }
        audio.preload = 'auto'
        audio.src = url
        audio.load()
      } catch { releaseUrl(); fallback(custom ? 'custom-audio-invalid' : 'manbo-audio') }
    }
    const cancel = () => {
      if (finished || controller.signal.aborted) return
      controller.abort(); finished = true
      report('cancelled', 'cancelled')
      clearNative(true); audioCleanup(); callbacks.preparing?.(false)
    }
    this.cancelPlayback = cancel
    if (custom || usesManbo(sentence.locale, sentence.preferences)) void recorded()
    else system()
    return cancel
  }
  dispose(): void { this.disposed = true; this.cancelPlayback?.(); this.voices.dispose(); this.manbo.dispose() }
}
